'use client';
import { useCallback, useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { useProduct } from '@/components/product/product-context';

export default function MicrositeSticky({ block }) {
  const { button_label, links } = block?.microsite_sticky?.reference[0];
  const [showSticky, setShowSticky] = useState(false);
  const [elementInView, setElementInView] = useState(links[0]?.anchor || null);
  const { productData } = useProduct();
  const visibleSection = useVisibleSection();

  const scrollListener = useCallback(() => {
    const currentCtaPosition =
      document?.querySelector('#microsite-hero-cta')?.getBoundingClientRect()
        ?.bottom + window.pageYOffset;
    setShowSticky(window.scrollY > currentCtaPosition);
  }, [setShowSticky]);

  useEffect(() => {
    window.addEventListener('scroll', scrollListener);
    return () => window.removeEventListener('scroll', scrollListener);
  }, [scrollListener]);

  useEffect(() => {
    if (links.find((link) => link.anchor == visibleSection)) {
      setElementInView(visibleSection);
    }
  }, [visibleSection, links]);

  const handleChange = (e) => {
    const selectedElement = document?.querySelector(e);
    if (selectedElement) {
      window.scrollTo({
        top: selectedElement.offsetTop - 64,
        left: 0,
        behavior: 'smooth'
      });
    }
  };

  const handleScroll = () => {
    const element = document.querySelector(
      '[data-name="microsite-add-to-cart"]'
    );
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };

  return (
    <>
      <section
        id="microsite-sticky"
        data-name="microsite-sticky"
        className={clsx(
          `fixed top-0 z-100 flex h-16 w-full items-center justify-between bg-white px-3 lg:px-15`,
          { hidden: !showSticky }
        )}
      >
        {/* Title */}
        <div className="h5 hidden lg:block">
          {productData?.shopifyProduct?.title}
        </div>
        {/* Links - Desktop */}
        <div className="body-s hidden space-x-6 lg:flex">
          {links?.map((link, index) => {
            return (
              <button
                key={index}
                href={link.anchor}
                className={
                  link.anchor == elementInView
                    ? 'decoration-cozy-cognac-20 decoration-padding-2 underline decoration-4 underline-offset-4'
                    : 'text-dark-grey-text hover:text-black'
                }
                onClick={() => handleChange(link?.anchor)}
              >
                {link.label}
              </button>
            );
          })}
        </div>
        {/* Links - Mobile */}
        {elementInView && (
          <select
            id="microsite-sticky-links"
            className={clsx('body-r mr-3 block border-none lg:hidden')}
            value={elementInView}
            onChange={(e) => handleChange(e.target.value)}
            data-cy="microsite-sticky-option-dropdown"
          >
            {links?.map((link, index) => (
              <option key={index} value={link.anchor}>
                {link.label}
              </option>
            ))}
          </select>
        )}
        {/* Button */}
        <button
          onClick={handleScroll}
          className="bg-warm-pewter body-r px-5 py-3"
        >
          {button_label}
        </button>
      </section>
    </>
  );
}

export function useVisibleSection() {
  const [visibleSection, setVisibleSection] = useState('');

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entryInView = entries.find((e) => e.isIntersecting);
        if (entryInView) {
          setVisibleSection(entryInView.target.id);
        }
      },
      {
        rootMargin: '-30% 0px',
        threshold: 0.2
      }
    );

    document
      .querySelectorAll('section[id]')
      .forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);
  return `#${visibleSection}`;
}
